import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  TemplateRef,
} from '@angular/core';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities/data';
import { GdprAnalyticsService } from '@recruitee/gdpr/data';
import { Ability, EmailTemplate, EmailTemplatesEventInvitation } from '@recruitee/http/types';
import { ConnectedOverlayDirective } from '@recruitee/user-interface';

import { RedactorComponent } from '../../redactor/redactor.component';
import { RedactorInsertsStep } from '../../redactor/types';
import { WysiwygLink, WysiwygLinkType } from '../../wysiwyg-extensions/link/wysiwyg-link.types';

@Component({
  selector: 'rt-redactor-inserts-button',
  templateUrl: './redactor-inserts-button.component.html',
  styleUrls: ['./redactor-inserts-button.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GdprAnalyticsService],
})
export class RedactorInsertsButtonComponent implements OnChanges {
  @Input() public inserts: RedactorInsertsStep[];
  @Input() public input?: RedactorComponent;
  @Input() public emailTemplateInsideTemplate?: TemplateRef<any>;

  @Output() public saveAsTemplate = new EventEmitter<void>();
  @Output() public emailTemplateChoose: EventEmitter<
    EmailTemplate | EmailTemplatesEventInvitation
  > = new EventEmitter();

  public canManageCandidates: boolean = false;
  public filteredInserts: RedactorInsertsStep[] | null = null;

  private canScheduleEvents = false;
  private ngOnDestroy$ = new Subject<void>();
  private REDACTOR_REQUIRED_INSERTS: RedactorInsertsStep[] = [
    'questionnaires-templates',
    'schedules',
    'gdpr-links',
  ];

  constructor(
    protected cdRef: ChangeDetectorRef,
    private gdprAnalyticsService: GdprAnalyticsService,
    private store: Store,
  ) {}

  public ngOnInit(): void {
    this.store
      .pipe(
        select(AbilitiesSelectors.hasOne(Ability.ScheduleEvents)),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(enabled => {
        this.canScheduleEvents = enabled;
        this.setFilteredInserts();
      });

    this.store
      .pipe(
        select(AbilitiesSelectors.hasOne(Ability.ManageCandidates)),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(enabled => {
        this.canManageCandidates = enabled;
      });
  }

  public ngOnChanges(): void {
    this.setFilteredInserts();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
  public openDropdown(event: MouseEvent, dropdownRef: ConnectedOverlayDirective): void {
    event.preventDefault();
    event.stopPropagation();
    dropdownRef.open = true;
    dropdownRef.updatePosition();
    this.cdRef.detectChanges();
  }

  public insertLink(link: WysiwygLink): void {
    if (!this.input) return;

    const linkType = link.type!;
    const isGdprLink = [
      WysiwygLinkType.GdprChange,
      WysiwygLinkType.GdprRemove,
      WysiwygLinkType.GdprConsentRequest,
    ].includes(linkType);
    if (isGdprLink) {
      const eventKind = <'correctionRequest' | 'removalRequest' | 'consentRequestLink'>{
        [WysiwygLinkType.GdprChange]: 'correctionRequest',
        [WysiwygLinkType.GdprRemove]: 'removalRequest',
        [WysiwygLinkType.GdprConsentRequest]: 'consentRequestLink',
      }[<WysiwygLinkType.GdprChange | WysiwygLinkType.GdprRemove | WysiwygLinkType.GdprConsentRequest>linkType];

      this.gdprAnalyticsService.trackInsertGDPRLink(eventKind);
    }

    this.input.restoreSelection();
    this.input.insertAtCaret(
      {
        ...link,
      },
      'link',
    );
  }

  public saveSelection(e: MouseEvent) {
    e.preventDefault();
    if (this.input) {
      this.input.saveSelection();
    }
  }

  private setFilteredInserts(): void {
    this.filteredInserts = this.canScheduleEvents
      ? this.inserts
      : this.inserts.filter(i => i !== 'schedules');

    this.filteredInserts = this.input
      ? this.filteredInserts
      : this.filteredInserts.filter(insert => !this.REDACTOR_REQUIRED_INSERTS.includes(insert));
  }
}
